'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-finance/workflows/expenses-management/capturing-expenses.mdx
 * (published at user-docs.totalaccess.co.za/total-finance/workflows/expenses-management/capturing-expenses)
 */

import React from 'react';
import { Search, Loader2, Building2, CheckCircle2, Briefcase, Layers, FileText, CalendarCheck, Car, Banknote, Fuel, Wrench, Route } from 'lucide-react';
import {
  JobSelector,
  VehicleSelector,
  SupplierPOSelector,
  type Supplier,
  type PurchaseOrder,
  type PoFulfillment,
  type PoLineItem,
} from './expense';
import type { Customer, Job } from './expense/useCustomerJobSearch';
import type { Vehicle } from './expense/useVehicleSearch';
import type { ChildEntryType } from '../types';

// ── Link to Appointment Toggle ──────────────────────────────────────────────
export interface LinkToAppointmentToggleProps {
  showLinkToggle: boolean;
  canLinkToWorkItem: boolean;
  linkToWorkItem: boolean;
  setLinkToWorkItem: (v: boolean) => void;
  isProcurementType: boolean;
  isDark: boolean;
  requiresPurchaseOrder: boolean;
  linkableWorkItemId: string | null | undefined;
  linkableWorkItemHasLinkedPOs: boolean;
  linkableWorkItemNumber: string | null | undefined;
  linkableWorkItemLocation: string | null | undefined;
  linkableWorkItemContactName: string | null | undefined;
  linkableWorkItemTitle: string | null | undefined;
  linkableWorkItemLabel: string | null | undefined;
  isJobLinkedExpense: boolean;
  // Inline customer dropdown + JobSelector props
  selectedCustomer: { id: number; company_name: string | null; contact_person: string | null } | null;
  setSelectedCustomer: (c: { id: number; company_name: string | null; contact_person: string | null } | null) => void;
  customerSearchQuery: string;
  setCustomerSearchQuery: (q: string) => void;
  showCustomerSearch: boolean;
  setShowCustomerSearch: (show: boolean) => void;
  loadingCustomers: boolean;
  filteredCustomers: Customer[];
  customerDropdownRef: React.RefObject<HTMLDivElement | null>;
  selectedJob: { id: string; item_number: string; title: string; contact_name?: string | null } | null;
  setSelectedJob: (j: { id: string; item_number: string; title: string; contact_name?: string | null } | null) => void;
  jobSearchQuery: string;
  setJobSearchQuery: (q: string) => void;
  showJobSearch: boolean;
  setShowJobSearch: (show: boolean) => void;
  loadingJobs: boolean;
  jobsChecked: boolean;
  filteredJobs: Job[];
  activeJobs: Job[];
  jobDropdownRef: React.RefObject<HTMLDivElement | null>;
}

function LinkToAppointmentToggle(props: LinkToAppointmentToggleProps) {
  const {
    showLinkToggle, canLinkToWorkItem, linkToWorkItem, setLinkToWorkItem,
    isProcurementType, requiresPurchaseOrder, linkableWorkItemId,
    linkableWorkItemHasLinkedPOs, linkableWorkItemNumber, linkableWorkItemLocation,
    linkableWorkItemContactName, linkableWorkItemTitle, linkableWorkItemLabel,
    isJobLinkedExpense, isDark,
    selectedCustomer, setSelectedCustomer, customerSearchQuery, setCustomerSearchQuery,
    showCustomerSearch, setShowCustomerSearch, loadingCustomers, filteredCustomers,
    customerDropdownRef, selectedJob, setSelectedJob, jobSearchQuery, setJobSearchQuery,
    showJobSearch, setShowJobSearch, loadingJobs, jobsChecked, filteredJobs, activeJobs,
    jobDropdownRef,
  } = props;

  if (!showLinkToggle) return null;

  const showCustomerDropdown = isJobLinkedExpense && !linkToWorkItem;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-3">
      <div className={`flex gap-3 ${showCustomerDropdown ? 'flex-row' : 'flex-col'}`}>
        {/* Square Appointment button (left side or full width) */}
        <div className={`flex flex-col gap-1.5 ${showCustomerDropdown ? 'shrink-0' : ''}`}>
          <button
            type="button"
            disabled={!canLinkToWorkItem}
            onClick={() => {
              if (!canLinkToWorkItem) return;
              setLinkToWorkItem(!linkToWorkItem);
              if (linkToWorkItem) {
                setSelectedCustomer(null);
                setSelectedJob(null);
              }
            }}
            className={`flex flex-col items-center gap-1.5 rounded-lg border py-2.5 px-3 text-xs font-medium transition-colors w-20 ${
              linkToWorkItem
                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-300'
                : isDark
                  ? 'border-gray-600 bg-gray-900/50 text-gray-400 hover:border-gray-500 hover:text-gray-200'
                  : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:text-gray-900'
            } ${!canLinkToWorkItem ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <CalendarCheck className="h-4 w-4" />
            Appointment
          </button>
          {!linkableWorkItemId ? (
            <p className="text-xs text-red-500 dark:text-red-400">No active appointment</p>
          ) : !linkableWorkItemHasLinkedPOs && isProcurementType && requiresPurchaseOrder ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">No linked POs</p>
          ) : linkToWorkItem ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {(() => {
                const parts: string[] = [];
                if (linkableWorkItemNumber) parts.push(linkableWorkItemNumber);
                if (isProcurementType) {
                  if (linkableWorkItemLocation) parts.push(linkableWorkItemLocation);
                } else {
                  if (linkableWorkItemContactName) parts.push(linkableWorkItemContactName);
                  if (linkableWorkItemLocation) parts.push(linkableWorkItemLocation);
                }
                const prefix = parts.join(' · ');
                if (linkableWorkItemTitle) {
                  return prefix ? `${prefix} — ${linkableWorkItemTitle}` : linkableWorkItemTitle;
                }
                return prefix || linkableWorkItemLabel || '';
              })()}
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Off — select customer</p>
          )}
        </div>

        {/* Customer dropdown (right side, same row as toggle) — only when toggle is off */}
        {showCustomerDropdown && (
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Customer *
            </label>
            {selectedCustomer ? (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                      {selectedCustomer.company_name || selectedCustomer.contact_person || 'Unnamed'}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setSelectedJob(null);
                    setCustomerSearchQuery('');
                    setJobSearchQuery('');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 shrink-0 ml-2"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative" ref={customerDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={customerSearchQuery}
                    onChange={(e) => {
                      setCustomerSearchQuery(e.target.value);
                      setShowCustomerSearch(true);
                    }}
                    onFocus={() => setShowCustomerSearch(true)}
                    className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                {showCustomerSearch && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    {loadingCustomers ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                        Loading customers...
                      </div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {customerSearchQuery.trim() ? 'No customers found' : 'No customers available'}
                      </div>
                    ) : (
                      <div>
                        {filteredCustomers.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setShowCustomerSearch(false);
                              setCustomerSearchQuery('');
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {customer.company_name || customer.contact_person || 'Unnamed'}
                            </div>
                            {customer.email && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{customer.email}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Job selector (below toggle+customer row, only when customer is selected and toggle is off) */}
      {showCustomerDropdown && selectedCustomer && (
        <JobSelector
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          jobSearchQuery={jobSearchQuery}
          setJobSearchQuery={setJobSearchQuery}
          showJobSearch={showJobSearch}
          setShowJobSearch={setShowJobSearch}
          loadingJobs={loadingJobs}
          jobsChecked={jobsChecked}
          filteredJobs={filteredJobs}
          activeJobs={activeJobs}
          jobDropdownRef={jobDropdownRef}
        />
      )}
    </div>
  );
}

// ── Link to Vehicle Toggle ──────────────────────────────────────────────────
export interface LinkToVehicleToggleProps {
  showVehicleLinkToggle: boolean;
  linkToVehicle: boolean;
  setLinkToVehicle: (v: boolean) => void;
  vehicleId: string | null | undefined;
  vehicleRegistration: string | null | undefined;
  searchedVehicle: Vehicle | null;
  setSearchedVehicle: (v: Vehicle | null) => void;
  vehicleSearchQuery: string;
  setVehicleSearchQuery: (q: string) => void;
  showVehicleSearch: boolean;
  setShowVehicleSearch: (show: boolean) => void;
  loadingVehicles: boolean;
  filteredVehicles: Vehicle[];
  vehicleDropdownRef: React.RefObject<HTMLDivElement | null>;
}

function LinkToVehicleToggle(props: LinkToVehicleToggleProps) {
  const {
    showVehicleLinkToggle, linkToVehicle, setLinkToVehicle, vehicleId, vehicleRegistration,
    searchedVehicle, setSearchedVehicle, vehicleSearchQuery, setVehicleSearchQuery,
    showVehicleSearch, setShowVehicleSearch, loadingVehicles, filteredVehicles,
    vehicleDropdownRef,
  } = props;

  if (!showVehicleLinkToggle) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <div className="relative inline-flex">
          <input
            type="checkbox"
            checked={linkToVehicle}
            disabled={!vehicleId}
            onChange={(e) => {
              setLinkToVehicle(e.target.checked);
              if (!e.target.checked) {
                setSearchedVehicle(null);
                setVehicleSearchQuery('');
                setShowVehicleSearch(false);
              }
            }}
            className="sr-only peer"
          />
          <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-600 peer-checked:bg-blue-600 ${!vehicleId ? 'opacity-50' : ''}`} />
        </div>
        <div className="flex-1">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Link to my vehicle</span>
          {linkToVehicle && vehicleRegistration ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">{vehicleRegistration}</p>
          ) : !vehicleId ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">No vehicle assigned — select a vehicle below</p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Toggle off to search for a different vehicle</p>
          )}
        </div>
      </label>

      {!linkToVehicle && (
        <VehicleSelector
          selectedVehicle={searchedVehicle}
          setSelectedVehicle={setSearchedVehicle}
          vehicleSearchQuery={vehicleSearchQuery}
          setVehicleSearchQuery={setVehicleSearchQuery}
          showVehicleSearch={showVehicleSearch}
          setShowVehicleSearch={setShowVehicleSearch}
          loadingVehicles={loadingVehicles}
          filteredVehicles={filteredVehicles}
          vehicleDropdownRef={vehicleDropdownRef}
        />
      )}
    </div>
  );
}

// ── Link to Supplier Toggle (General Expense, not job-linked) ────────────────
export interface LinkToSupplierToggleProps {
  selectedEntryType: ChildEntryType;
  isJobLinkedExpense: boolean;
  linkToSupplier: boolean;
  setLinkToSupplier: (v: boolean) => void;
  selectedSupplier: Supplier | null;
  setSelectedSupplier: (s: Supplier | null) => void;
  supplierSearchQuery: string;
  setSupplierSearchQuery: (q: string) => void;
  showSupplierSearch: boolean;
  setShowSupplierSearch: (show: boolean) => void;
  filteredSuppliers: Supplier[];
  loadingSuppliers: boolean;
  supplierDropdownRef: React.RefObject<HTMLDivElement | null>;
}

function LinkToSupplierToggle(props: LinkToSupplierToggleProps) {
  const {
    selectedEntryType, isJobLinkedExpense, linkToSupplier, setLinkToSupplier,
    selectedSupplier, setSelectedSupplier, supplierSearchQuery, setSupplierSearchQuery,
    showSupplierSearch, setShowSupplierSearch, filteredSuppliers, loadingSuppliers,
    supplierDropdownRef,
  } = props;

  if (selectedEntryType !== 'purchase' || isJobLinkedExpense) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <div className="relative inline-flex">
          <input
            type="checkbox"
            checked={linkToSupplier}
            onChange={(e) => {
              setLinkToSupplier(e.target.checked);
              if (!e.target.checked) {
                setSelectedSupplier(null);
                setSupplierSearchQuery('');
                setShowSupplierSearch(false);
              }
            }}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-600 peer-checked:bg-blue-600" />
        </div>
        <div className="flex-1">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Link to supplier</span>
          <p className="text-xs text-gray-500 dark:text-gray-400">Toggle on to select a supplier before capturing</p>
        </div>
      </label>

      {linkToSupplier && (
        <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Supplier *
            </label>
            {selectedSupplier ? (
              <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.company_name || selectedSupplier.name}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSupplier(null);
                    setSupplierSearchQuery('');
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative" ref={supplierDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search suppliers..."
                    value={supplierSearchQuery}
                    onChange={(e) => {
                      setSupplierSearchQuery(e.target.value);
                      setShowSupplierSearch(true);
                    }}
                    onFocus={() => setShowSupplierSearch(true)}
                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                {showSupplierSearch && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    {loadingSuppliers ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                        Loading suppliers...
                      </div>
                    ) : filteredSuppliers.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {supplierSearchQuery.trim() ? 'No suppliers found' : 'No suppliers available'}
                      </div>
                    ) : (
                      <div>
                        {filteredSuppliers.map((supplier) => (
                          <button
                            key={supplier.id}
                            type="button"
                            onClick={() => {
                              setSelectedSupplier(supplier);
                              setShowSupplierSearch(false);
                              setSupplierSearchQuery('');
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {supplier.company_name || supplier.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pre-capture Supplier + PO Selection (procurement, not linked to appointment) ──
export interface ProcurementPreCaptureProps {
  isProcurementType: boolean;
  linkToWorkItem: boolean;
  isFormVisible: boolean;
  requiresPurchaseOrder: boolean;
  selectedSupplier: Supplier | null;
  setSelectedSupplier: (s: Supplier | null) => void;
  supplierSearchQuery: string;
  setSupplierSearchQuery: (q: string) => void;
  showSupplierSearch: boolean;
  setShowSupplierSearch: (show: boolean) => void;
  filteredSuppliers: Supplier[];
  loadingSuppliers: boolean;
  selectedPurchaseOrder: PurchaseOrder | null;
  setSelectedPurchaseOrder: (po: PurchaseOrder | null) => void;
  poSearchQuery: string;
  setPoSearchQuery: (q: string) => void;
  poSearchResults: PurchaseOrder[];
  isSearchingPO: boolean;
  showPOSearch: boolean;
  setShowPOSearch: (show: boolean) => void;
  procurementSetupExpanded: boolean;
  setProcurementSetupExpanded: (expanded: boolean) => void;
  poLineItems: PoLineItem[];
  poFulfillment: PoFulfillment | null;
  supplierDropdownRef: React.RefObject<HTMLDivElement | null>;
  poDropdownRef: React.RefObject<HTMLDivElement | null>;
  selectedEntryType: ChildEntryType;
  onClearPO: () => void;
}

function ProcurementPreCapture(props: ProcurementPreCaptureProps) {
  if (!props.isProcurementType || props.linkToWorkItem || props.isFormVisible) return null;

  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
        {props.requiresPurchaseOrder
          ? 'Select the supplier and purchase order before capturing the document.'
          : 'Select the supplier before capturing the document.'}
      </div>
      <SupplierPOSelector
        selectedSupplier={props.selectedSupplier}
        setSelectedSupplier={props.setSelectedSupplier}
        supplierSearchQuery={props.supplierSearchQuery}
        setSupplierSearchQuery={props.setSupplierSearchQuery}
        showSupplierSearch={props.showSupplierSearch}
        setShowSupplierSearch={props.setShowSupplierSearch}
        filteredSuppliers={props.filteredSuppliers}
        loadingSuppliers={props.loadingSuppliers}
        selectedPurchaseOrder={props.selectedPurchaseOrder}
        setSelectedPurchaseOrder={props.setSelectedPurchaseOrder}
        poSearchQuery={props.poSearchQuery}
        setPoSearchQuery={props.setPoSearchQuery}
        poSearchResults={props.poSearchResults}
        isSearchingPO={props.isSearchingPO}
        showPOSearch={props.showPOSearch}
        setShowPOSearch={props.setShowPOSearch}
        procurementSetupExpanded={props.procurementSetupExpanded}
        setProcurementSetupExpanded={props.setProcurementSetupExpanded}
        requiresPurchaseOrder={props.requiresPurchaseOrder}
        poLineItems={props.poLineItems}
        poFulfillment={props.poFulfillment}
        supplierDropdownRef={props.supplierDropdownRef}
        poDropdownRef={props.poDropdownRef}
        selectedEntryType={props.selectedEntryType || ''}
        onClearPO={props.onClearPO}
      />
    </div>
  );
}

// ── Post-capture Supplier + PO (procurement, linked to appointment) ──────────
export interface ProcurementPostCaptureProps {
  isProcurementType: boolean;
  linkToWorkItem: boolean;
  aiSuggestingSupplier: boolean;
  aiSupplierMatch: { contact_id: number; contact_name: string; confidence: number; match_method: string } | null;
  requiresPurchaseOrder: boolean;
  selectedSupplier: Supplier | null;
  setSelectedSupplier: (s: Supplier | null) => void;
  supplierSearchQuery: string;
  setSupplierSearchQuery: (q: string) => void;
  showSupplierSearch: boolean;
  setShowSupplierSearch: (show: boolean) => void;
  filteredSuppliers: Supplier[];
  loadingSuppliers: boolean;
  selectedPurchaseOrder: PurchaseOrder | null;
  setSelectedPurchaseOrder: (po: PurchaseOrder | null) => void;
  poSearchQuery: string;
  setPoSearchQuery: (q: string) => void;
  poSearchResults: PurchaseOrder[];
  isSearchingPO: boolean;
  showPOSearch: boolean;
  setShowPOSearch: (show: boolean) => void;
  procurementSetupExpanded: boolean;
  setProcurementSetupExpanded: (expanded: boolean) => void;
  poLineItems: PoLineItem[];
  poFulfillment: PoFulfillment | null;
  supplierDropdownRef: React.RefObject<HTMLDivElement | null>;
  poDropdownRef: React.RefObject<HTMLDivElement | null>;
  selectedEntryType: ChildEntryType;
  onClearPO: () => void;
}

function ProcurementPostCapture(props: ProcurementPostCaptureProps) {
  if (!props.isProcurementType || !props.linkToWorkItem) return null;

  return (
    <>
      {/* ── AI Supplier Match Banner (procurement types, linked to appointment — capture-first) ── */}
      {(props.aiSuggestingSupplier || props.aiSupplierMatch) && (
        <div className={`rounded-lg border px-3 py-2 text-xs flex items-center gap-2 ${
          props.aiSuggestingSupplier
            ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
            : (props.aiSupplierMatch?.confidence ?? 0) >= 0.7
              ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
              : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
        }`}>
          {props.aiSuggestingSupplier ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
              <span className="text-blue-700 dark:text-blue-300">AI is matching supplier…</span>
            </>
          ) : props.aiSupplierMatch ? (
            <>
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-500" />
              <span className="text-green-700 dark:text-green-300">
                AI matched supplier: <strong>{props.aiSupplierMatch.contact_name}</strong> ({Math.round(props.aiSupplierMatch.confidence * 100)}% confidence, {props.aiSupplierMatch.match_method})
              </span>
            </>
          ) : null}
        </div>
      )}

      {/* ── Supplier + PO Selection (procurement types, linked to appointment — after capture) ── */}
      <SupplierPOSelector
      selectedSupplier={props.selectedSupplier}
      setSelectedSupplier={props.setSelectedSupplier}
      supplierSearchQuery={props.supplierSearchQuery}
      setSupplierSearchQuery={props.setSupplierSearchQuery}
      showSupplierSearch={props.showSupplierSearch}
      setShowSupplierSearch={props.setShowSupplierSearch}
      filteredSuppliers={props.filteredSuppliers}
      loadingSuppliers={props.loadingSuppliers}
      selectedPurchaseOrder={props.selectedPurchaseOrder}
      setSelectedPurchaseOrder={props.setSelectedPurchaseOrder}
      poSearchQuery={props.poSearchQuery}
      setPoSearchQuery={props.setPoSearchQuery}
      poSearchResults={props.poSearchResults}
      isSearchingPO={props.isSearchingPO}
      showPOSearch={props.showPOSearch}
      setShowPOSearch={props.setShowPOSearch}
      procurementSetupExpanded={props.procurementSetupExpanded}
      setProcurementSetupExpanded={props.setProcurementSetupExpanded}
      requiresPurchaseOrder={props.requiresPurchaseOrder}
      poLineItems={props.poLineItems}
      poFulfillment={props.poFulfillment}
      supplierDropdownRef={props.supplierDropdownRef}
      poDropdownRef={props.poDropdownRef}
      selectedEntryType={props.selectedEntryType || ''}
      onClearPO={props.onClearPO}
    />
    </>
  );
}

// ── Linking Mode Selector (6-button grid, 2 rows of 3, for Smart Capture) ───
// Replaces the old 4-button "Link to" selector. Adds Vehicle Expense and
// Casual Wages as direct entry points. Vehicle Expense is only shown when
// the user has a vehicle assigned. Vehicle expenses are automatically locked
// to the assigned vehicle (no toggle/search).
export type LinkingMode = 'supplier' | 'job' | 'both' | 'general' | 'vehicle' | 'wages' | null;

// Vehicle sub-type selected after tapping "Vehicle Expense"
export type VehicleExpenseSubType = 'fuel' | 'toll' | 'maintenance' | null;

export interface LinkingModeSelectorProps {
  linkingMode: LinkingMode;
  setLinkingMode: (mode: LinkingMode) => void;
  isDark: boolean;
  /** When true, user has a vehicle assigned — shows the Vehicle Expense button */
  hasVehicleAssigned?: boolean;
  /** Vehicle sub-type for the "vehicle" linking mode (fuel/toll/maintenance) */
  vehicleSubType?: VehicleExpenseSubType;
  /** Callback to set the vehicle sub-type when the user picks Fuel/Toll/Maintenance */
  setVehicleSubType?: (sub: VehicleExpenseSubType) => void;
  /** When true, lock the selector to vehicle expense mode only (Fuel/Toll/Maintenance).
   *  Hides the 6-button selector and shows only the vehicle sub-type picker. */
  lockToVehicleExpense?: boolean;
  /** RBAC-filtered set of permitted modes (expenses-management workflow
   *  matrix rows). null/undefined = no filtering (legacy behaviour — the
   *  caller is not governed by the expenses workflow matrix). */
  allowedModes?: ReadonlySet<Exclude<LinkingMode, null>> | null;
  /** Hide the 6-button mode grid entirely (a permissioned workflow tile
   *  forced the linking mode). The vehicle sub-type picker still renders
   *  when the forced mode is 'vehicle'. */
  hideModeButtons?: boolean;
}

export function LinkingModeSelector({
  linkingMode,
  setLinkingMode,
  isDark,
  hasVehicleAssigned = false,
  vehicleSubType = null,
  setVehicleSubType,
  lockToVehicleExpense = false,
  allowedModes = null,
  hideModeButtons = false,
}: LinkingModeSelectorProps) {
  // ── Highlight sweep: cycle through buttons on mount to draw attention ──
  const [highlightIndex, setHighlightIndex] = React.useState<number | null>(null);

  // Build button list dynamically — Vehicle Expense is hidden when no vehicle assigned
  // `lines` controls multi-line label display: two-word labels split across two lines,
  // except "Supplier & Job" which stays on one line.
  // When locked to vehicle expense, only the Vehicle Expense button is visible.
  const isModeAllowed = (mode: Exclude<LinkingMode, null>) =>
    allowedModes == null || allowedModes.has(mode);

  const allButtons: Array<{ mode: LinkingMode; lines: string[]; icon: React.ReactNode; visible: boolean }> = [
    { mode: 'supplier', lines: ['Supplier', 'Document'], icon: <Building2 className="h-4 w-4" />, visible: !lockToVehicleExpense && !hideModeButtons && isModeAllowed('supplier') },
    { mode: 'job', lines: ['Job', 'Expense'], icon: <Briefcase className="h-4 w-4" />, visible: !lockToVehicleExpense && !hideModeButtons && isModeAllowed('job') },
    { mode: 'both', lines: ['Supplier & Job'], icon: <Layers className="h-4 w-4" />, visible: !lockToVehicleExpense && !hideModeButtons && isModeAllowed('both') },
    { mode: 'general', lines: ['General', 'Expense'], icon: <FileText className="h-4 w-4" />, visible: !lockToVehicleExpense && !hideModeButtons && isModeAllowed('general') },
    { mode: 'vehicle', lines: ['Vehicle', 'Expense'], icon: <Car className="h-4 w-4" />, visible: hasVehicleAssigned && !hideModeButtons && isModeAllowed('vehicle') },
    { mode: 'wages', lines: ['Casual', 'Wages'], icon: <Banknote className="h-4 w-4" />, visible: !lockToVehicleExpense && !hideModeButtons && isModeAllowed('wages') },
  ];
  const buttons = allButtons.filter((b) => b.visible);

  React.useEffect(() => {
    let cancelled = false;
    const HIGHLIGHT_MS = 250; // x2 faster than original 500ms
    const TOTAL_CYCLES = 3;
    const totalSteps = buttons.length * TOTAL_CYCLES;
    for (let step = 0; step < totalSteps; step++) {
      setTimeout(() => {
        if (cancelled) return;
        setHighlightIndex(step % buttons.length);
      }, step * HIGHLIGHT_MS);
    }
    // Clear after the last highlight
    setTimeout(() => {
      if (cancelled) return;
      setHighlightIndex(null);
    }, totalSteps * HIGHLIGHT_MS);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVehicleAssigned]);

  // Split into 2 rows of 3
  const row1 = buttons.slice(0, 3);
  const row2 = buttons.slice(3, 6);

  const renderButton = (btn: { mode: LinkingMode; lines: string[]; icon: React.ReactNode }, idx: number) => {
    const isActive = linkingMode === btn.mode;
    const isHighlighted = highlightIndex === idx;
    return (
      <button
        key={btn.mode}
        type="button"
        onClick={() => {
          setLinkingMode(isActive ? null : btn.mode);
        }}
        className={`flex flex-col items-center gap-1 rounded-lg border py-2 px-1 text-xs font-medium transition-all duration-200 ${
          isActive || isHighlighted
            ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-300'
            : isDark
              ? 'border-gray-600 bg-gray-900/50 text-gray-400 hover:border-gray-500 hover:text-gray-200'
              : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:text-gray-900'
        }`}
      >
        {btn.icon}
        {btn.lines.map((line, i) => (
          <span key={i} className="leading-tight">{line}</span>
        ))}
      </button>
    );
  };

  // Vehicle sub-selector (Fuel/Toll/Maintenance) — shown when "Vehicle Expense" is active
  const vehicleSubButtons: Array<{ sub: VehicleExpenseSubType; label: string; icon: React.ReactNode }> = [
    { sub: 'fuel', label: 'Fuel', icon: <Fuel className="h-4 w-4" /> },
    { sub: 'toll', label: 'Toll', icon: <Route className="h-4 w-4" /> },
    { sub: 'maintenance', label: 'Maintenance', icon: <Wrench className="h-4 w-4" /> },
  ];

  // RBAC gate active but every mode denied — show a notice instead of an
  // empty grid so the user understands why nothing is selectable.
  if (buttons.length === 0 && allowedModes != null && !lockToVehicleExpense && !hideModeButtons) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          You do not have permission to capture expenses. Contact your administrator.
        </p>
      </div>
    );
  }

  // Forced non-vehicle mode — nothing left to render (no mode buttons, no
  // vehicle sub-type picker).
  if (hideModeButtons && linkingMode !== 'vehicle') {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-2">
      {row1.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {row1.map((btn, idx) => renderButton(btn, idx))}
        </div>
      )}
      {row2.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {row2.map((btn, idx) => renderButton(btn, idx + 3))}
        </div>
      )}
      {/* Vehicle sub-selector — appears when Vehicle Expense is selected */}
      {linkingMode === 'vehicle' && setVehicleSubType && (
        <div className="pt-1">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Select vehicle expense type</p>
          <div className="grid grid-cols-3 gap-2">
            {vehicleSubButtons.map((vbtn) => {
              const isActive = vehicleSubType === vbtn.sub;
              return (
                <button
                  key={vbtn.sub}
                  type="button"
                  onClick={() => setVehicleSubType(vbtn.sub)}
                  className={`flex flex-col items-center gap-1.5 rounded-lg border py-2 px-1 text-xs font-medium transition-all duration-200 ${
                    isActive
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-300'
                      : isDark
                        ? 'border-gray-600 bg-gray-900/50 text-gray-400 hover:border-gray-500 hover:text-gray-200'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  {vbtn.icon}
                  {vbtn.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Standalone Supplier Dropdown (used by linking mode 'supplier' and 'both') ──
export interface SupplierDropdownProps {
  selectedSupplier: Supplier | null;
  setSelectedSupplier: (s: Supplier | null) => void;
  supplierSearchQuery: string;
  setSupplierSearchQuery: (q: string) => void;
  showSupplierSearch: boolean;
  setShowSupplierSearch: (show: boolean) => void;
  filteredSuppliers: Supplier[];
  loadingSuppliers: boolean;
  supplierDropdownRef: React.RefObject<HTMLDivElement | null>;
}

export function SupplierDropdown(props: SupplierDropdownProps) {
  const {
    selectedSupplier, setSelectedSupplier, supplierSearchQuery, setSupplierSearchQuery,
    showSupplierSearch, setShowSupplierSearch, filteredSuppliers, loadingSuppliers,
    supplierDropdownRef,
  } = props;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Supplier *
        </label>
        {selectedSupplier ? (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {selectedSupplier.company_name || selectedSupplier.name}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedSupplier(null);
                setSupplierSearchQuery('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative" ref={supplierDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search suppliers..."
                value={supplierSearchQuery}
                onChange={(e) => {
                  setSupplierSearchQuery(e.target.value);
                  setShowSupplierSearch(true);
                }}
                onFocus={() => setShowSupplierSearch(true)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
            {showSupplierSearch && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {loadingSuppliers ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                    Loading suppliers...
                  </div>
                ) : filteredSuppliers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {supplierSearchQuery.trim() ? 'No suppliers found' : 'No suppliers available'}
                  </div>
                ) : (
                  <div>
                    {filteredSuppliers.map((supplier) => (
                      <button
                        key={supplier.id}
                        type="button"
                        onClick={() => {
                          setSelectedSupplier(supplier);
                          setShowSupplierSearch(false);
                          setSupplierSearchQuery('');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {supplier.company_name || supplier.name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export {
  LinkToAppointmentToggle,
  LinkToVehicleToggle,
  LinkToSupplierToggle,
  ProcurementPreCapture,
  ProcurementPostCapture,
};
